import { Link } from 'react-router-dom'
import AdminActionBar from '../../components/admin/AdminActionBar'
import PageMasthead from '../../components/PageMasthead'
import { CLASSIFIERS } from './mockData'
import { WIREFRAME_PATHS } from './routes'
import ModalityPill from '../../components/badges/ModalityPill'
import VersionCell from '../../components/classifier/VersionCell'
import WireframeBanner from './WireframeBanner'
import { PageDesignNote } from './designNotes'

// Map the first version's date field to a human-readable "Updated" display string.
// The mockup shows static strings per row so we use them verbatim where known.
const UPDATED_LABEL: Record<string, string> = {
    'hate-harassment': '2d ago',
    'spam-scams': '3w ago',
    'self-harm': '5d ago',
    'brand-risk-imagery': '1mo ago',
    'competitor-mentions': '6d ago'
}

export default function ClassifierLibraryPage() {
    return (
        <div className="w-full bg-gray-50 min-h-full">
            <WireframeBanner />

            <PageDesignNote type="new" reachableFrom="the Admin sidebar">
                A central library of comment classifiers. Classifiers are applied to profiles only
                — adding one to a team or organisation just bulk-adds it to the profiles beneath,
                so usage here is simply a count of profiles.
            </PageDesignNote>

            <PageMasthead
                title="Classifier configuration"
                subtitle="24 classifiers · 312 profiles covered"
                actions={
                    <button
                        type="button"
                        className="rounded-lg bg-arwen-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-arwen-700"
                    >
                        + New classifier
                    </button>
                }
            />

            <AdminActionBar>
                <input
                    type="text"
                    placeholder="Search classifiers…"
                    aria-label="Search classifiers"
                    className="flex-1 max-w-xs rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-arwen-500"
                />
                <select aria-label="Filter by modality" className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-arwen-500">
                    <option>All modalities</option>
                    <option>Text</option>
                    <option>Image</option>
                    <option>Video</option>
                </select>
                <select aria-label="Filter by status" className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-arwen-500">
                    <option>Any status</option>
                    <option>Has draft</option>
                    <option>Live only</option>
                </select>
                <select aria-label="Filter by organisation" className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-arwen-500">
                    <option>All organisations</option>
                    <option>Acme Corp</option>
                    <option>Globex</option>
                    <option>Initech</option>
                </select>
            </AdminActionBar>

            <div className="px-6 py-4 overflow-auto">
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full border-collapse bg-white text-sm">
                    <thead>
                        <tr>
                            <th scope="col" className="bg-gray-50 px-3 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200">
                                Classifier
                            </th>
                            <th scope="col" className="bg-gray-50 px-3 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200">
                                Modality
                            </th>
                            <th scope="col" className="bg-gray-50 px-3 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200">
                                Coverage
                            </th>
                            <th scope="col" className="bg-gray-50 px-3 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200">
                                Version
                            </th>
                            <th scope="col" className="bg-gray-50 px-3 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200">
                                Updated
                            </th>
                            <th scope="col" className="bg-gray-50 px-3 py-2.5 border-b border-gray-200" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {CLASSIFIERS.map((c) => {
                            const draftVersion = c.versions.find(
                                (v) => v.status === 'draft'
                            )?.version

                            return (
                                <tr
                                    key={c.id}
                                    className="hover:bg-gray-50"
                                >
                                    {/* Classifier name + description */}
                                    <td className="px-3 py-3 align-top">
                                        <div className="flex items-center gap-1.5">
                                            <span className="font-semibold text-gray-900">
                                                {c.title}
                                            </span>
                                            {c.custom && (
                                                <span className="inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-gray-600 ring-1 ring-inset ring-gray-300/40">
                                                    Custom
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-0.5 max-w-xs text-xs text-gray-500">
                                            {c.description}
                                        </div>
                                    </td>

                                    {/* Modality pill */}
                                    <td className="px-3 py-3 align-top">
                                        <ModalityPill modality={c.modality} />
                                    </td>

                                    {/* Coverage — applications are per profile, so this is
                                        just profile reach (teams derived for context) */}
                                    <td className="px-3 py-3 align-top text-gray-700">
                                        {c.profilesUsing}{' '}
                                        {c.profilesUsing === 1 ? 'profile' : 'profiles'}{' '}
                                        <span className="text-gray-400">
                                            · {c.teamsUsing}{' '}
                                            {c.teamsUsing === 1 ? 'team' : 'teams'}
                                        </span>
                                    </td>

                                    {/* Version */}
                                    <td className="px-3 py-3 align-top">
                                        <VersionCell live={c.liveVersion} draft={draftVersion} />
                                    </td>

                                    {/* Updated */}
                                    <td className="px-3 py-3 align-top text-gray-500">
                                        {UPDATED_LABEL[c.id] ?? '—'}
                                    </td>

                                    {/* Actions */}
                                    <td className="px-3 py-3 align-top">
                                        <Link
                                            to={WIREFRAME_PATHS.edit}
                                            className="text-arwen-600 hover:text-arwen-800 text-sm font-medium"
                                        >
                                            Edit
                                        </Link>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
                </div>
            </div>
        </div>
    )
}
